"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { rijderHref, favorietSectie, type SectieCode } from "@/lib/secties";
import { FavorietKnop } from "@/components/favoriet-knop";
import type { KlassementSectie, KlassementRij } from "@/server/klassement";
import type { DeelnemerSoort } from "@/server/wedstrijden";

const SOORT_LABEL: Record<DeelnemerSoort, string> = {
  competitie: "Competitie",
  eurocup: "Euro Cup",
  overig: "Overig",
};
const SOORT_VOLGORDE: DeelnemerSoort[] = ["competitie", "eurocup", "overig"];

function soortenIn(sectie: KlassementSectie): DeelnemerSoort[] {
  return SOORT_VOLGORDE.filter((s) => sectie.klassen.some((k) => k.standen.some((r) => r.soort === s)));
}
export function KlassementLijst({ secties }: { secties: KlassementSectie[] }) {
  const [sectieIdx, setSectieIdx] = useState(0);
  const sectie = secties[sectieIdx] ?? secties[0];
  const soorten = sectie ? soortenIn(sectie) : [];
  const [soort, setSoort] = useState<DeelnemerSoort>(soorten[0] ?? "competitie");
  // Alles dichtgeklapt beginnen: een open klasse met tientallen rijders duwt de
  // volgende blokken ver onder de vouw, waardoor het lijkt alsof die niet opengaan.
  const [openKlasse, setOpenKlasse] = useState<string | null>(null);

  if (!sectie) {
    return (
      <p className="px-4 py-10 text-center text-sm text-muted-foreground">
        Voor dit seizoen is nog geen klassement bekend.
      </p>
    );
  }

  const zichtbaar = sectie.klassen
    .map((k) => ({ ...k, standen: k.standen.filter((r) => r.soort === soort) }))
    .filter((k) => k.standen.length > 0);

  function kiesSectie(i: number) {
    const s = secties[i];
    const so = soortenIn(s);
    setSectieIdx(i);
    setSoort(so[0] ?? "competitie");
    setOpenKlasse(null);
  }
  function kiesSoort(s: DeelnemerSoort) {
    setSoort(s);
    setOpenKlasse(null);
  }

  return (
    <div>
      {secties.length > 1 && (
        <div className="mb-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {secties.map((s, i) => (
            <button
              key={s.code}
              type="button"
              onClick={() => kiesSectie(i)}
              className={cn(
                "shrink-0 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wide transition-colors",
                i === sectieIdx ? "bg-primary text-primary-foreground" : "bg-card text-white/70 hover:text-white",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {soorten.length > 1 && (
        <div className="mb-4 flex gap-2 px-4">
          {soorten.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => kiesSoort(s)}
              className={cn(
                "rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ring-1 transition-colors",
                s === soort ? "text-primary ring-primary" : "text-white/50 ring-white/15 hover:text-white/80",
              )}
            >
              {SOORT_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-3 px-4">
        {zichtbaar.map((k) => {
          const open = openKlasse === k.key;
          return (
            <div key={k.key} className="overflow-hidden rounded-xl border border-border bg-card">
              {/* De ster naast de uitklapknop, niet erin: een knop in een knop
                  is geen geldige opmaak, en je zou bij het volgen de klasse
                  open- en dichtklappen. De sleutel van de klasse is dezelfde
                  die de uitslagen gebruiken ("fs-12", "gn-3"). */}
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setOpenKlasse(open ? null : k.key)}
                  aria-expanded={open}
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 py-3.5 pl-4 text-left"
                >
                  <span className="min-w-0 truncate font-display text-base font-bold uppercase tracking-wide text-white">
                    {k.naam}
                  </span>
                  <span className="flex shrink-0 items-center gap-2.5">
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-bold tabular-nums text-primary">
                      {k.standen.length}
                    </span>
                    <ChevronDown
                      className={cn(
                        "size-5 text-primary transition-transform duration-200",
                        open && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </span>
                </button>
                <FavorietKnop
                  favoriet={{ soort: "klasse", sleutel: k.key, naam: k.naam }}
                  lijst
                />
              </div>

              {open && (
                <ul className="divide-y divide-white/5 border-t border-white/5">
                  {k.standen.map((r, i) => (
                    <li key={i}>
                      <StandRij
                        r={r}
                        href={rijderHref(sectie.code, r.deelnID)}
                        sectie={sectie.code}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StandRij({
  r,
  href,
  sectie,
}: {
  r: KlassementRij;
  href: string | null;
  sectie: SectieCode;
}) {
  const podium = r.positie <= 3;
  const punten = r.punten.toLocaleString("nl-NL", { maximumFractionDigits: 1 });
  const inhoud = (
    <>
      <div
        className={cn(
          "flex size-9 shrink-0 items-center justify-center rounded-lg font-display text-base font-black tabular-nums",
          podium ? "bg-primary text-primary-foreground" : "bg-muted text-primary",
        )}
      >
        {r.positie}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-white">{r.titel}</p>
        {r.ondertitel && <p className="truncate text-xs text-muted-foreground">{r.ondertitel}</p>}
      </div>
      <span className="shrink-0 text-right">
        <span className="font-display text-base font-black tabular-nums text-white">{punten}</span>
        <span className="ml-1 text-xs text-muted-foreground">ptn</span>
      </span>
    </>
  );

  // De ster naast de link, niet erin: een knop in een link is geen geldige
  // opmaak, en je zou bij het volgen doorklikken naar het profiel.
  const ster =
    r.deelnID != null ? (
      <FavorietKnop
        sectie={favorietSectie(sectie)}
        id={r.deelnID}
        naam={r.titel}
        lijst
        smal
      />
    ) : null;

  if (!href) {
    return (
      <div className="flex items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3">{inhoud}</div>
        {ster}
      </div>
    );
  }
  return (
    <div className="flex items-center">
      <Link
        href={href}
        className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3 transition-colors active:bg-white/5"
      >
        {inhoud}
      </Link>
      {ster}
    </div>
  );
}
